import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AkaTypography, LoadingSpinner, Table } from 'components'
import { Col } from 'antd'
import {
	ContentCheckProofStatus,
	ReportStatus,
	TenantType
} from 'generated/types'
import { ROUTE_FUNCTIONS } from 'modules/navigation'
import {
	GroupSection,
	GroupHeaderRow,
	GroupTitle,
	TitleLink,
	UnderlinedBoldText
} from '../styles'
import StatusCell from './StatusCell'
import BadgeCell from './BadgeCell'
import ActionsCell from './ActionsCell'
import SystemApprovalStatusInline from './SystemApprovalStatusInline'
import type { ColumnsType } from 'antd/lib/table'

type Tenant = TenantType

type RowBase = {
	key: string
	id: string
	title: string
	badge?: string | null
	deadline?: string | null
	tenant?: Tenant | null
	selectedSystemId?: string
	catalogTypeCode?: string | null
	catalogTypeLabel?: string
}

type ReportRow = RowBase & {
	__type: 'report'
	status: ReportStatus
}

type ContentCheckRow = RowBase & {
	__type: 'contentCheck'
	status: ContentCheckProofStatus
}

type RowData = ReportRow | ContentCheckRow

type ReportLike = {
	id: string
	status?: ReportStatus | null
	approvalRun?: {
		tenant?: Tenant | null
		submissionDeadline?: string | null
		catalogType?: { code?: string | null; displayTitle?: string | null } | null
	} | null
}

type ContentCheckLike = {
	id: string
	title?: string | null
	proof?: { status?: ContentCheckProofStatus | null } | null
	approvalRun?: {
		tenant?: Tenant | null
		submissionDeadline?: string | null
		catalogType?: { code?: string | null; displayTitle?: string | null } | null
	} | null
}

export type ApprovalTableProps = {
	reportList?: ReportLike[]
	reportsLoading?: boolean
	contentCheckList?: ContentCheckLike[]
	contentChecksLoading?: boolean
	selectedSystemId?: string
	quarter?: string
	year?: string
}

/**
 * ApprovalTable
 *
 * Displays a merged list of approvals-related items (reports and content checks) in grouped tables,
 * grouped by catalog type. Each group shows the system approval status inline per tenant, and the
 * corresponding items in a compact table without headers.
 */
const ApprovalTable: React.FC<ApprovalTableProps> = ({
	reportList = [],
	reportsLoading = true,
	contentCheckList = [],
	contentChecksLoading = true,
	selectedSystemId,
	quarter,
	year
}) => {
	const [t] = useTranslation()

	// Build unified rows for both reports and content checks.
	// For missing values, fallback to safe defaults and localized placeholders.
	const rows: RowData[] = useMemo(
		() => [
			...(reportList || []).map<RowData>(report => ({
				key: `report-${report.id}`,
				id: report.id,
				__type: 'report',
				title: t('approval.approvalList.reportCard.title'),
				badge: report?.approvalRun?.tenant?.name,
				deadline: report?.approvalRun?.submissionDeadline,
				status: report?.status ?? ReportStatus.OPEN,
				tenant: report?.approvalRun?.tenant,
				selectedSystemId,
				catalogTypeCode: report?.approvalRun?.catalogType?.code,
				catalogTypeLabel:
					report?.approvalRun?.catalogType?.displayTitle ||
					report?.approvalRun?.catalogType?.code ||
					t('approval.approvalList.unknownCatalogType')
			})),
			...(contentCheckList || []).map<RowData>(contentCheck => ({
				key: `cc-${contentCheck.id}`,
				id: contentCheck.id,
				__type: 'contentCheck',
				title: `${t('approval.approvalList.contentCheckCard.title')}: ${
					contentCheck.title ?? ''
				}`,
				badge: contentCheck?.approvalRun?.tenant?.name,
				deadline: contentCheck?.approvalRun?.submissionDeadline,
				status: contentCheck?.proof?.status ?? ContentCheckProofStatus.OPEN,
				tenant: contentCheck?.approvalRun?.tenant,
				selectedSystemId,
				catalogTypeCode: contentCheck?.approvalRun?.catalogType?.code,
				catalogTypeLabel:
					contentCheck?.approvalRun?.catalogType?.displayTitle ||
					contentCheck?.approvalRun?.catalogType?.code ||
					t('approval.approvalList.unknownCatalogType')
			}))
		],
		[reportList, contentCheckList, selectedSystemId, t]
	)

	// Column definitions for the compact table. Most columns use `render` to customize output.
	const columns: ColumnsType<RowData> = [
		{
			dataIndex: 'title',
			key: 'title',
			render: (text: string, row: RowData) => (
				<UnderlinedBoldText variant="body2">
					<TitleLink
						to={
							row.__type === 'contentCheck'
								? ROUTE_FUNCTIONS.getContentCheckDetailsPath(
										quarter || '',
										row.id,
										row.selectedSystemId || ''
									)
								: ROUTE_FUNCTIONS.getSystemInfoPath(quarter || '', row.id)
						}
					>
						{text}
					</TitleLink>
				</UnderlinedBoldText>
			)
		},
		{
			dataIndex: 'badge',
			key: 'badge',
			render: (_: unknown, row: RowData) => <BadgeCell tenant={row.tenant} />
		},
		{
			dataIndex: 'deadline',
			key: 'deadline',
			render: (date?: string | null) => (
				<span>
					{t('approval.approvalList.table.deadline')}
					{date ? new Date(date).toLocaleDateString('de-DE') : '-'}
				</span>
			)
		},
		{
			dataIndex: 'status',
			key: 'status',
			render: (status: ReportStatus | ContentCheckProofStatus) => (
				<StatusCell status={status} />
			)
		},
		{
			key: 'actions',
			render: (_: unknown, row: RowData) => <ActionsCell row={row} />
		}
	]

	// Group rows by their catalog type label. Unknown catalog type falls back to a localized placeholder.
	const groupedRows = useMemo(() => {
		return rows.reduce<Record<string, RowData[]>>(
			(acc, row) => {
				const groupKey: string =
					row.catalogTypeLabel ?? t('approval.approvalList.unknownCatalogType')
				if (!acc[groupKey]) {
					acc[groupKey] = []
				}
				acc[groupKey].push(row)
				return acc
			},
			{} as Record<string, RowData[]>
		)
	}, [rows, t])

	if (reportsLoading || contentChecksLoading) return <LoadingSpinner fullPage />
	if (rows.length === 0) {
		return (
			<AkaTypography data-testid="no-results" variant="body">
				{t('approval.approvalList.noResults')}
			</AkaTypography>
		)
	}

	return (
		<>
			{Object.entries(groupedRows).map(([catalogTypeLabel, groupRows]) => (
				<GroupSection key={catalogTypeLabel}>
					<GroupHeaderRow>
						<Col span={7}>
							<GroupTitle variant="h3">{catalogTypeLabel}</GroupTitle>
						</Col>

						<SystemApprovalStatusInline
							systemId={selectedSystemId}
							quarter={quarter}
							year={year}
							catalogTypeCode={groupRows[0]?.catalogTypeCode ?? undefined}
							tenantsInGroup={Array.from(
								new Map(
									groupRows
										.filter(r => r.tenant?.id)
										.map(r => [r.tenant?.id, r.tenant])
								).values()
							)}
						/>
					</GroupHeaderRow>
					<Table
						columns={columns}
						dataSource={groupRows}
						pagination={false}
						showHeader={false}
					/>
				</GroupSection>
			))}
		</>
	)
}

export default ApprovalTable
